import { Form, FormikProps } from "formik"
import { Formik } from "../../../../../../shared/components/forms/SchemaFormik"
import { isPossiblePhoneNumber } from "libphonenumber-js"
import { Ref, forwardRef, useMemo } from "react"
import * as Yup from "yup"

import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { History } from "../../../../../users/userTypes"

import { TenantUserFormValues, defaultTenantUserFormValues } from "../tenantUserTypes"
import { TenantUserFormContent } from "./TenantUserFormContent"

type TenantUserFormProps = Readonly<{
    isEditMode: boolean
    initialValues?: TenantUserFormValues
    history?: History
    onSubmit: (values: TenantUserFormValues) => void
}>

type FormikRef = Ref<FormikProps<TenantUserFormValues>>

export const TenantUserForm = forwardRef(
    ({ isEditMode, initialValues, history, onSubmit }: TenantUserFormProps, ref: FormikRef) => {
        const { l10n } = useAppLocalization()

        const validationSchema = useMemo(
            () =>
                Yup.object().shape({
                    username: Yup.string(),
                    employeeId: Yup.string(),
                    displayName: Yup.string().required(
                        l10n.getString("office-manager-create-user-required-field-empty"),
                    ),
                    email: Yup.string()
                        .email(l10n.getString("office-manager-create-user-invalid-email-address"))
                        .required(l10n.getString("office-manager-create-user-required-field-empty")),
                    mobileNumber: Yup.string().test({
                        name: "is-valid-phonenumber",
                        test: (value) => (value ? isPossiblePhoneNumber(value, "DK") : true),
                        message: l10n.getString("office-manager-create-user-invalid-phone-number"),
                    }),
                    phoneNumber: Yup.string().test({
                        name: "is-valid-phonenumber",
                        test: (value) => (value ? isPossiblePhoneNumber(value, "DK") : true),
                        message: l10n.getString("office-manager-create-user-invalid-phone-number"),
                    }),
                    sendEmailInvitation: Yup.bool(),
                    ignoreUserSyncJobs: Yup.bool(),
                    isActive: Yup.bool(),
                    workgroups: Yup.array(),
                }),
            [l10n],
        )

        return (
            <Formik<TenantUserFormValues>
                enableReinitialize
                initialValues={initialValues ?? defaultTenantUserFormValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
                innerRef={ref}
            >
                <Form>
                    <TenantUserFormContent isEditMode={isEditMode} history={history} />
                </Form>
            </Formik>
        )
    },
)
